import { Injectable, inject } from '@angular/core';
import { BuildingType, CountryCode, FormKeys, HeatLoadCalculationMethod, ProjectType } from '@nx-customer-apps/shared/enums';
import { CalculationMethodOptions, FormOptions, QuestionOptions } from '@nx-customer-apps/shared/interfaces';
import { calculationMethodValidator, CustomAnswerRadio, RadioQuestion, TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { RollOutPolicy } from '../../../../policies';
import { FormToolsService } from '../../../../services/form-tools.service';
import { ValidatorsService } from '../../../../services/validators.service';
import { ProjectsService } from '../../../../services/projects.service';
import { AuthService } from 'apps/viguide-planning/src/app/services';

@Injectable({
    providedIn: 'root'
})
export class CalculationMethodFormService {
    private rollOutPolicy = inject(RollOutPolicy);
    private authService = inject(AuthService);
    private formToolsService = inject(FormToolsService);
    private projectsService = inject(ProjectsService);
    private validatorsService = inject(ValidatorsService);

    /*
     * Adds "by consumption" option to radio question options if project kind is "renovation".
     */
    public getCalculationMethodQuestions(options: FormOptions): [CustomAnswerRadio] {
        const calculationMethod = this.calculationMethodQuestion(options);
        return [calculationMethod];
    }

    private calculationMethodQuestion(options: CalculationMethodOptions): CustomAnswerRadio {
        const definedValueOverride = options?.questions?.[FormKeys.DefinedValue];
        const standardMethodOverride = options?.questions?.[FormKeys.StandardMethod];

        if (!options.countryCode) {
            throw Error('Please specify countryCode for calculation method question.');
        }

        const livingSpaceOption = {
            value: HeatLoadCalculationMethod.LivingSpace,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_LIVING_AREA',
            disabled: this.isLivingSpaceOptionDisabled(options.countryCode, options.projectType!)
        };
        const consumptionOption = {
            value: HeatLoadCalculationMethod.Consumption,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_CONSUMPTION'
        };
        const buildingHullOption = {
            value: HeatLoadCalculationMethod.BuildingHull,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_BUILDING_HULL',
            disabled: this.isBuildingHullOptionDisabled(options.countryCode, options.projectType!)
        };
        const customValueOption = {
            value: HeatLoadCalculationMethod.CustomValue,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_CUSTOM_VALUE'
        };
        const energyCertificateOption = {
            value: HeatLoadCalculationMethod.EnergyCertificate,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_ENERGY_CERT'
        };
        const deviceDataOption = {
            value: HeatLoadCalculationMethod.Installation,
            key: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_INSTALLATION'
        };

        const newConstructionOptions = [livingSpaceOption, energyCertificateOption, buildingHullOption, customValueOption];
        const renovationOptions = [livingSpaceOption, consumptionOption, buildingHullOption, customValueOption];

        if (this.rollOutPolicy.hasDeviceCalculation(options.countryCode) && this.authService.isInstaller()) {
            const customValuIndex = renovationOptions.indexOf(customValueOption);
            renovationOptions.splice(customValuIndex, 0, deviceDataOption);
        }

        const radioOptions = options.projectType === ProjectType.Renovation ? renovationOptions : newConstructionOptions;

        const standardMethodOptions: QuestionOptions<string> = {
            key: FormKeys.StandardMethod,
            value: standardMethodOverride?.value,
            disabled: standardMethodOverride?.disabled,
            options: radioOptions
        };

        const standardMethods = new RadioQuestion(standardMethodOptions);

        if (!options.buildingType) {
            throw Error('Please specify buildingType for calculation method question.');
        }

        const maskSettings = {
            [BuildingType.SingleFamilyHouse]: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '1' }),
            [BuildingType.MultiFamilyHouse]: this.formToolsService.getNumericMask({ separatorLimit: '1000', numberOfDecimals: '1' })
        };

        const selectedMask = maskSettings[options.buildingType];

        const minMaxSettings = {
            [BuildingType.SingleFamilyHouse]: this.validatorsService.minMaxValidator({ min: 0, max: 50 }),
            [BuildingType.MultiFamilyHouse]: this.validatorsService.minMaxValidator({ min: 5, max: 2000 })
        };

        const selectedMinMaxValidator = minMaxSettings[options.buildingType];

        const definedValueOptions: QuestionOptions<string> = {
            key: FormKeys.DefinedValue,
            value: definedValueOverride?.value,
            styles: 'ca-form-field-medium',
            placeholder: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_ENTER_OWN_VALUE',
            mask: selectedMask,
            suffix: 'MEASUREMENT_UNITS.KW',
            validators: [selectedMinMaxValidator],
            defaultErrorMessage: 'HEATLOAD.CALCULATION_METHOD.ERRORS.REQUIRED_OWN_VALUE',
            permanentHint: 'HEATLOAD.CALCULATION_METHOD.CALC_BY_ENTER_OWN_VALUE_HINT'
        };

        const definedValue = new TextboxQuestion(definedValueOptions);

        return new CustomAnswerRadio({
            groupName: FormKeys.Methods,
            customAnswerOptionValue: HeatLoadCalculationMethod.CustomValue,
            styles: 'mb-4 ca-custom-answer-radio',
            validators: [calculationMethodValidator],
            questions: [standardMethods, definedValue]
        });
    }

    private isLivingSpaceOptionDisabled(countryCode: CountryCode, projectType: ProjectType): boolean {
        return this.projectsService.isRenovation(projectType) && !this.rollOutPolicy.hasLivingAreaCalcuatiion(countryCode);
    }

    private isBuildingHullOptionDisabled(countryCode: CountryCode, projectType: ProjectType): boolean {
        return this.projectsService.isNewConstruction(projectType) && !this.rollOutPolicy.hasBuildingHullCalculation(countryCode);
    }
}
